import { ThreadPrimitive } from '@assistant-ui/react';
import ChatComposer from 'components/Chat/ChatComposer';
import ChatMessages from 'components/Chat/ChatMessages';
import ChatPromptSuggestions from 'components/Chat/ChatPromptSuggestions';
import { useTranslation } from 'react-i18next';
import { Button, Icon, ScrollFadeContainer, Stack, styled, Text, Tooltip } from 'shell/components';
import { ChatLayout } from 'utils/chat/chatConstants';

const CHAT_PANEL_SPACING = '20px';
const CHAT_BG_COLOR = '#FCFCFC';

const ChatPanelView = ({
    afterHeaderContent = null,
    assistantIdentity = null,
    composer,
    error,
    header,
    messageLayout = ChatLayout.DEFAULT,
    suggestions,
    thread,
    threadFallback = null,
}) => {
    const { t } = useTranslation('assignment');

    return (
        <RootStack>
            <HeaderRow>
                {header.showBackButton ? (
                    <BackButton startIcon={<Icon kind={'arrowLeft'} />} variant={'borderless'} onClick={header.onBack}>
                        {t('common:controls.back', 'Back')}
                    </BackButton>
                ) : null}
                <CenteredTitleRow
                    alignItems={'center'}
                    direction={'row'}
                    isTitleCentered={header.isTitleCentered}
                    spacing={1}
                >
                    {!header.showBackButton && header.startContent ? header.startContent : null}
                    <Stack sx={{ width: 'inherit' }} direction={'row'} alignItems='center' spacing={2}>
                        {header.icon && (
                            <HeaderIconBadge>
                                <Icon kind={header.icon} />
                            </HeaderIconBadge>
                        )}
                        <Text component={'h2'} sx={{ textAlign: 'center' }} weight={'semibold'} noWrap>
                            {header.title}
                        </Text>
                    </Stack>
                    {header.infoTooltip ? <Tooltip content={header.infoTooltip} type={'info'} /> : null}
                </CenteredTitleRow>
                {header.endContent ? <HeaderEndSlot>{header.endContent}</HeaderEndSlot> : null}
            </HeaderRow>
            {afterHeaderContent}

            <ScrollFadeContainer fadeColor={CHAT_BG_COLOR} fadeHeight={'48px'} showTop={thread.showTopScrollGradient}>
                {threadFallback || (
                    <ThreadViewport
                        {...thread.viewportProps}
                        onKeyDown={thread.onKeyDown}
                        onScroll={thread.onScroll}
                        onTouchMove={thread.onTouchMove}
                        onWheel={thread.onWheel}
                    >
                        {thread.isLoadingOlderMessages ? (
                            <ViewportTopStatus
                                alignItems={'center'}
                                direction={'row'}
                                justifyContent={'center'}
                                spacing={1}
                            >
                                <ViewportLoadingSpinner aria-hidden={'true'} />
                                <ViewportLoadingText variant={'p3'}>
                                    {t('chat.loading-older', 'Loading Older Messages...')}
                                </ViewportLoadingText>
                            </ViewportTopStatus>
                        ) : null}
                        {thread.emptyState.isVisible ? (
                            <ThreadEmptyState>
                                <Text variant={'sh1'}>
                                    Hello
                                    {thread.emptyState.firstName ? (
                                        <>
                                            {' '}
                                            <Text color={'primary'} component={'span'} variant={'sh1'}>
                                                {thread.emptyState.firstName}
                                            </Text>
                                        </>
                                    ) : null}
                                    ,
                                    <br />
                                    {thread.emptyState.body}
                                </Text>
                            </ThreadEmptyState>
                        ) : (
                            <ChatMessages
                                {...thread.messagesProps}
                                assistantIdentity={assistantIdentity}
                                layout={messageLayout}
                            />
                        )}
                        {suggestions.isVisible ? (
                            <SuggestionsContainer>
                                <ChatPromptSuggestions
                                    disabled={suggestions.disabled}
                                    suggestions={suggestions.items}
                                    title={suggestions.title}
                                    variant={suggestions.variant}
                                    onSelect={suggestions.onSelect}
                                />
                            </SuggestionsContainer>
                        ) : null}
                    </ThreadViewport>
                )}
            </ScrollFadeContainer>

            {composer.isVisible ? (
                <ComposerDock>
                    {error.message ? (
                        <ChatErrorBanner spacing={5}>
                            <Text color={'error'}>{error.message}</Text>
                            {error.action ? (
                                <Button
                                    startIcon={error.action.icon ? <Icon kind={error.action.icon} /> : null}
                                    variant={'secondary'}
                                    onClick={error.onAction}
                                >
                                    {error.action.label}
                                </Button>
                            ) : null}
                        </ChatErrorBanner>
                    ) : null}
                    <ChatComposer {...composer.props} />
                </ComposerDock>
            ) : null}
        </RootStack>
    );
};

const RootStack = styled(Stack)(() => {
    return {
        flex: 1,
        padding: CHAT_PANEL_SPACING,
        background: CHAT_BG_COLOR,
        minHeight: 0,
        maxWidth: '100%',
        fontSize: '14px',

        '& .MuiButton-root': {
            fontSize: '14px',
        },
        '& .MuiInputBase-input, & textarea': {
            fontSize: '14px',
        },
    };
});

const HeaderRow = styled(Stack)(({ theme }) => {
    return {
        position: 'relative',
        minHeight: theme.spacing(5),
        justifyContent: 'center',
        paddingBottom: theme.spacing(3),
    };
});

const BackButton = styled(Button)(() => {
    return {
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1,
    };
});

const HeaderIconBadge = styled('span')(({ theme }) => {
    return {
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        background: theme.palette.ensightful.primary[50],
        color: theme.palette.ensightful.primary[600],
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
    };
});

const HeaderEndSlot = styled(Stack)(({ theme }) => {
    return {
        position: 'absolute',
        right: 0,
        zIndex: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(1),
    };
});

const CenteredTitleRow = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'isTitleCentered',
})(({ theme, isTitleCentered }) => {
    return {
        width: isTitleCentered ? 'auto' : 'calc(100% - 52px)',
        flexWrap: 'nowrap',
        padding: isTitleCentered ? theme.spacing(0, 10) : 0,
        justifyContent: isTitleCentered ? 'center' : 'flex-start',
    };
});

const ThreadViewport = styled(ThreadPrimitive.Viewport)(({ theme }) => {
    return {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        marginRight: `-${CHAT_PANEL_SPACING}`,
        paddingRight: CHAT_PANEL_SPACING,
    };
});

const ThreadEmptyState = styled(Stack)(({ theme }) => {
    return {
        flex: 1,
        padding: theme.spacing(5, 0),
        whiteSpace: 'pre-line',
    };
});

const ViewportTopStatus = styled(Stack)(({ theme }) => {
    return {
        position: 'sticky',
        top: 4,
        zIndex: 1,
        gap: theme.spacing(2),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        background: CHAT_BG_COLOR,
    };
});

const ViewportLoadingText = styled(Text)(({ theme }) => {
    return {
        color: theme.palette.ensightful.primary[500],
    };
});

const ViewportLoadingSpinner = styled('span')(({ theme }) => {
    return {
        width: theme.spacing(2),
        height: theme.spacing(2),
        borderRadius: '50%',
        border: `2px solid ${theme.palette.ensightful.primary[100]}`,
        borderTopColor: theme.palette.ensightful.primary[500],
        animation: 'chat-loading-spin 0.8s linear infinite',

        '@keyframes chat-loading-spin': {
            '0%': {
                transform: 'rotate(0deg)',
            },
            '100%': {
                transform: 'rotate(360deg)',
            },
        },
    };
});

const ComposerDock = styled(Stack)(({ theme }) => {
    return {
        gap: theme.spacing(5),
    };
});

const ChatErrorBanner = styled(Stack)(({ theme }) => {
    return {
        alignItems: 'flex-start',
        padding: theme.spacing(2),
    };
});

const SuggestionsContainer = styled(Stack)(() => {
    return {
        width: '100%',
        alignItems: 'flex-end',
        flexShrink: 0,
        paddingBottom: '16px',
    };
});

export default ChatPanelView;
